import { createSlice } from "@reduxjs/toolkit";
import {
  fetchRelativesByUserId,
  createRelative,
  updateRelative,
  acceptRelative,
  deleteRelative,
} from "../thunks/relativesThunks";

/* =============================
 * INITIAL STATE
 * ============================= */
const initialState = {
  data: [],
  loading: false,
  error: null,
  hasFetched: false,
};

/* =============================
 * Helpers
 * ============================= */
const getId = (r) =>
  r?._id || r?.id || r?.relativeId || r?.linkedUserId;

/* =============================
 * SLICE
 * ============================= */
const relativesSlice = createSlice({
  name: "relatives",
  initialState,
  reducers: {
    resetRelatives: () => ({ ...initialState }),
    clearRelativesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      /* ================= FETCH ================= */
      .addCase(fetchRelativesByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchRelativesByUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.hasFetched = true;

        const incoming = Array.isArray(action.payload)
          ? action.payload
          : [];

        // 🔁 Merge sans perdre les données enrichies côté front
        const map = new Map();
        state.data.forEach((r) => map.set(getId(r), r));
        incoming.forEach((r) => map.set(getId(r), {
          ...map.get(getId(r)),
          ...r,
        }));

        state.data = Array.from(map.values());
      })

      .addCase(fetchRelativesByUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.hasFetched = true;
      })

      /* ================= CREATE ================= */  
      .addCase(createRelative.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(createRelative.fulfilled, (state, action) => {
        state.loading = false;

        // ⚠️ invitation email → backend retourne null
        if (!action.payload) return;

        const created = action.payload;
        const request = action.meta.arg; // 🔥 payload envoyé au backend
        const id = getId(created);

        // 🧠 ENRICHISSEMENT FRONT - Garder relativeUser du backend
        const enriched = {
          ...created,
          // ✅ Conserver relativeUser du backend (prioritaire)
          relativeUser: created.relativeUser,
          // ✅ Fallback pour les cas où relativeUser est null
          relativeSearch: created.relativeUser 
            ? undefined 
            : (request.relativeSearch ?? created.relativeSearch),
          relativeEmail: created.relativeUser 
            ? undefined 
            : (request.relativeEmail ?? created.relativeEmail),
        };

        const exists = state.data.some(
          (r) => getId(r) === id
        );

        if (!exists) {
          state.data.unshift(enriched);
        } else {
          // ✅ Mettre à jour l'existant avec les nouvelles données enrichies
          state.data = state.data.map((r) =>
            getId(r) === id ? { ...r, ...enriched } : r
          );
        }
      })

      .addCase(createRelative.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ================= UPDATE ================= */
      .addCase(updateRelative.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = getId(updated);

        state.data = state.data.map((r) =>
          getId(r) === id ? { ...r, ...updated } : r
        );
      })

      /* ================= ACCEPT ================= */
      .addCase(acceptRelative.fulfilled, (state, action) => {
        const accepted = action.payload;
        const id = getId(accepted);

        state.data = state.data.map((r) =>
          getId(r) === id ? { ...r, ...accepted } : r
        );
      })

      /* ================= DELETE ================= */
      .addCase(deleteRelative.fulfilled, (state, action) => {
        state.data = state.data.filter(
          (r) => getId(r) !== action.payload
        );
      });
  },
});

export const {
  resetRelatives,
  clearRelativesError,
} = relativesSlice.actions;

export default relativesSlice.reducer;


